import React, { useContext, useEffect, useRef, useState } from 'react';
import { useInfiniteHits, useInstantSearch } from 'react-instantsearch';
import ResultsSkeleton from '../collections/ResultsSkeleton';
import GtmContext from '../../../contexts/GtmContext';
import qs from 'qs';
import useSearchRedirectURL from '../../../components/search/useSearchRedirectURL';
import { dataLayerPushEventActionWithCategory } from '../pdp/utils/utils';
import { initWishlist } from '../../../snippets/customer.wishlist';
import { useProductActionsContext } from '@jbhi-fi/components/src/context/ProductActionsProvider';
import { useProductCompare } from '@jbhi-fi/components/src/ProductCompare';
import { useCustomerContext } from '@jbhi-fi/components/src/context/CustomerContextProvider';
import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { Disclaimer } from '@jbhi-fi/components/src/Disclaimer';
import { SearchProductCardWrapper } from '../../../components/search/SearchProductCardWrapper';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag/useFeatureFlag';
import { getStoreCurrency } from '@jbhi-fi/utils/src/shopify/getStoreCurrency';
import { GIFTCARDSKU } from '../../../utils/gift-card.js';
import { trackViewItemList } from '@jbhi-fi/utils/src/ga4Tracking/functions/trackViewItemList';
import { transformAlgoliaHitForTracking } from '@jbhi-fi/components/src/Algolia/transformers/transformAlgoliaHitForTracking';
import { SessionStorageKeys, getSession } from '@jbhi-fi/utils/src/sessionStorage';
import { pushToDataLayer } from '@jbhi-fi/utils/src/ga4Tracking/datalayer';
import { addInsight, postAlgoliaProductClick, postAlgoliaProductView } from '../../../utils/algolia/search-insights';
import { paginateHits } from '../../../utils/algolia/paginateHits';
import { ProductCardVariant } from '@jbhi-fi/components/src/ProductCard/ProductCard';
import { useCartContext } from '../../../utils/cart/context/CartContext';
import { NoResultsView } from '../collections/NoResultsView';

const DEBOUNCE_TIME = 400;

const ProgressBar = (props: { completed: string }) => {
  const { completed } = props;

  const fillerStyles = {
    height: '100%',
    width: `${completed}%`,
    backgroundColor: '#FFEC0F',
    transition: 'width 1s ease-in-out',
    borderRadius: 'inherit',
    textAlign: 'right' as const,
  };

  return (
    <div className="progress-bar-container">
      <div style={fillerStyles}></div>
    </div>
  );
};

type InfiniteResultsProps = {
  nbHits: number;
  page: number;
  hits: Array<AlgoliaProductHit>;
  showMore: () => void;
  isLastPage: boolean;
  isLoading: boolean;
  productCardVariant: ProductCardVariant;
  isProductCardDetailsEnabled?: boolean;
  isProductCardDetailBadgesEnabled?: boolean;
};

const InfiniteResults = ({
  nbHits,
  page,
  hits,
  showMore,
  isLastPage,
  isLoading,
  productCardVariant,
  isProductCardDetailsEnabled = false,
  isProductCardDetailBadgesEnabled = false,
}: InfiniteResultsProps) => {
  const debouncedSetStateRef = useRef<any | null>(null);
  const lastGtmRef = useRef(null);
  const lastNbHitsRef = useRef(nbHits);
  const lastPageRef = useRef(page);
  const { gtm, setGtm } = useContext(GtmContext) as { gtm: any; setGtm: any };
  const searchState = qs.parse(window.location.search.slice(1));
  const filters = Object.keys(searchState)
    .filter(key => !['query', 'page', 'hitsPerPage', 'sortBy'].includes(key))
    .reduce((obj, key) => {
      if (key.toLowerCase() === 'price') {
        obj[key.toLowerCase()] = { type: 'range', value: searchState[key] };
      } else {
        obj[key.toLowerCase()] = Array.isArray(searchState[key])
          ? (searchState[key] as Array<string>).map(i => ({ type: 'checkbox', value: i }))
          : { type: 'checkbox', value: searchState[key] };
      }
      return obj;
    }, {} as any);
  const { onAddToCart, productCartPropsOverrides = {} } = useProductActionsContext();
  const { isAddedToCompare, compareHandleClick, shouldShowCompare } = useProductCompare(false);
  const { isLoggedIn, handleUpdateWishlist, getIsInWishlist } = useCustomerContext();
  const [resultsFilters, setResultsFilters] = useState({});
  const [trackedSkus, setTrackedSkus] = useState<Array<string>>([]);
  const { cart } = useCartContext();

  useEffect(() => {
    if (nbHits !== lastNbHitsRef.current || page !== lastPageRef.current) {
      lastNbHitsRef.current = nbHits;
      lastPageRef.current = page;

      setGtm({
        ...gtm,
        search: {
          ...gtm.search,
          count: nbHits,
        },
        ecommerce: {
          ...gtm.ecommerce,
          currencyCode: getStoreCurrency(),
          impressions: hits.map((c, i) => ({
            ...c.google_tag_manager,
            position: i + 1,
            list: 'search results',
          })),
        },
      });
    }
  }, [page, nbHits]);

  useEffect(() => {
    const results = hits.map(hit => hit.google_tag_manager);
    if (
      window.gtm.searchProducts &&
      results &&
      results[0] &&
      !window.gtm.searchProducts.some((p: any) => Number(p.id) === Number(results[0].id))
    ) {
      window.gtm.searchProducts = [...window.gtm.searchProducts, results[0]];
    } else {
      window.gtm.searchProducts = results;
    }
  }, [hits]);

  useEffect(() => {
    if (!lastGtmRef.current && gtm.search && gtm.search.count && gtm.ecommerce && gtm.ecommerce.impressions) {
      clearTimeout(debouncedSetStateRef.current);

      debouncedSetStateRef.current = setTimeout(() => {
        // Push gtm view event
        window.dataLayer.push({
          ...gtm,
          pageType: 'Search Pages',
          event: 'custom.product.list.view',
          action: 'view',
          label: undefined,
          data: undefined,
          filters: filters,
          info: {
            url: window.location.href,
          },
        });

        // Set last gtm state
        lastGtmRef.current = gtm;
      }, DEBOUNCE_TIME);
    }
  }, [gtm]);

  useEffect(() => {
    const resultsFilters: { category?: string; price?: { min: string; max: string }; saleItems?: boolean } = {};
    const paginatedHits = paginateHits(hits, 36);

    if (filters.category?.value) {
      resultsFilters.category = filters.category.value;
    }
    if (filters.price?.value?.min || filters.price?.value?.max) {
      resultsFilters.price = {
        min: filters.price?.value?.min ?? null,
        max: filters.price?.value?.max ?? null,
      };
    }
    if (filters.saleItems?.value === 'true') {
      resultsFilters.saleItems = true;
    }

    setResultsFilters(resultsFilters);

    const untrackedHits: Array<AlgoliaProductHit> = [];
    paginatedHits.forEach(page => {
      page?.forEach(hit => {
        const sku = hit.sku || hit.product?.id;
        if (sku && !trackedSkus.includes(sku)) {
          untrackedHits.push(hit);
          setTrackedSkus(prev => [...prev, sku]);
        }
      });
    });

    if (untrackedHits.length) {
      const chunkSize = 20;
      for (let i = 0; i < untrackedHits.length; i += chunkSize) {
        const chunk = untrackedHits.slice(i, i + chunkSize);
        pushToDataLayer({
          event: 'view_search_results',
          count: untrackedHits.length,
          searchType: 'site search',
          method: getSession(SessionStorageKeys.SearchMethod),
          item_list_name: `search - all results`,
          filters: {
            price: {
              'Min Price': resultsFilters.price?.min,
              'Max Price': resultsFilters.price?.max,
            },
            'Sale items': filters.saleitems?.value === 'true' ? 'true' : undefined,
          },
        });
        trackViewItemList({
          count: chunk.length,
          searchTerm: searchState.query?.toString(),
          searchType: 'site search',
          searchMethod: getSession(SessionStorageKeys.SearchMethod),
          resultsFilters: resultsFilters,
          currency: getStoreCurrency(),
          listName: 'search - all results',
          viewedItems: transformAlgoliaHitForTracking(chunk),
        });
        postAlgoliaProductView(chunk);
      }
    }
  }, [hits]);

  const scrollToTop = () => {
    window.scrollTo(0, 0);
    dataLayerPushEventActionWithCategory('plpuparrowclick', 'search', 'click', gtm.search);
  };

  const gtmClick = (hit: AlgoliaProductHit, i: number) => {
    window.dataLayer.push({
      ...gtm,
      event: 'custom.product.list.click',
      label: hit.title,
      data: undefined,
      ecommerce: {
        ...gtm.ecommerce,
        click: {
          actionField: {
            list: 'search results',
          },
          products: [
            {
              ...hit.google_tag_manager,
              position: i + 1,
              list: 'search results',
            },
          ],
        },
      },
    });
  };

  useEffect(() => {
    (window.dataLayer.push({
      pageType: 'Search Pages',
    }),
      window.gtm.pageType === 'Search Pages' ? '' : (window.gtm.pageType = 'Search Pages'));
  }, []);

  initWishlist();

  return (
    <div>
      <div className={`search-results-loop search-results-loop--${productCardVariant}`}>
        {hits.map((hit, i) => (
          <SearchProductCardWrapper
            {...productCartPropsOverrides}
            shouldHideCartOverlay={productCartPropsOverrides.shouldHideCartOverlay || GIFTCARDSKU === hit.sku}
            variant={productCardVariant}
            type="results"
            position={i + 1}
            key={hit.objectID}
            hit={hit}
            onClick={sku => {
              gtmClick(hit, i);
              addInsight(sku, hit.__queryID);
              postAlgoliaProductClick(sku, i + 1, hit.__queryID);
            }}
            getIsCompared={isAddedToCompare}
            onCompare={compareHandleClick}
            getShouldShowCompare={shouldShowCompare}
            isLoggedIn={isLoggedIn}
            onUpdateWishlist={handleUpdateWishlist}
            getIsInWishlist={getIsInWishlist}
            extraTracking={{
              searchTerm: searchState.query?.toString(),
              searchMethod: getSession(SessionStorageKeys.SearchMethod),
              resultsFilters: resultsFilters,
            }}
            onAddToCart={
              onAddToCart
                ? async (e: any, sku: any, variantId: any, eventData: any) => {
                    const isSuccess = await onAddToCart(e, hit, variantId, eventData?.trackingDescriptor);
                    if (isSuccess) {
                      addInsight(hit.sku, hit.__queryID, cart);
                    }
                    return isSuccess;
                  }
                : undefined
            }
            isProductCardDetailsEnabled={isProductCardDetailsEnabled}
            isProductCardDetailBadgesEnabled={isProductCardDetailBadgesEnabled}
            isDynamicWidth={true}
          />
        ))}
      </div>
      <div className="infinite-hits-loadmore">
        <div className="infinite-hits-text">
          Showing {hits.length} of {nbHits} results
        </div>
        <ProgressBar completed={`${(hits.length / nbHits) * 100}`} />
        {!isLastPage && isLoading ? (
          <div className="spinner" />
        ) : (
          <button className="load-more-button" disabled={isLastPage} onClick={showMore}>
            Load more
          </button>
        )}
        <button className="scroll-up-button" onClick={scrollToTop}>
          <div className="arrow-up"></div>
        </button>
      </div>
    </div>
  );
};

const MemoizedInfiniteResults = React.memo(InfiniteResults);

const CustomInfiniteHits = () => {
  const { status, results } = useInstantSearch();
  const { showMore, isLastPage, hits } = useInfiniteHits<AlgoliaProductHit>();
  const nbHits = results?.nbHits ?? 0;
  const page = results?.page ?? 0;
  const hasResults = nbHits > 0;
  const isLoading = status === 'loading' || status === 'stalled';
  const isRedirecting = useSearchRedirectURL(results, true, true);
  const isInitialLoad = results?.__isArtificial;

  const shouldUse2upMobileTiles = useFeatureFlag('shouldUse2upMobileTiles');
  const isHorizontalProductCardEnabled = useFeatureFlag('isHorizontalProductCardEnabled');
  const isProductCardDetailsEnabled = useFeatureFlag('isProductCardDetailsEnabled');
  const isProductCardDetailBadgesEnabled = useFeatureFlag('isProductCardDetailBadgesEnabled');

  const productCardVariant = (() => {
    if (isHorizontalProductCardEnabled) return 'landscape';
    if (shouldUse2upMobileTiles) return 'tablet';
    return 'default';
  })();

  if ((!hasResults && isLoading) || isRedirecting) {
    return (
      <div className={`search-results-loop search-results-loop--${productCardVariant}`}>
        <ResultsSkeleton shouldShimmer variant={productCardVariant} />
      </div>
    );
  }

  if (!isInitialLoad && !hasResults && !isLoading) {
    return (
      <NoResultsView title="Sorry, we weren’t able to find any results for your search.">
        <button onClick={() => document.querySelector<HTMLInputElement>('#quicksearch-search-box input')?.click()}>
          Start a new search
        </button>

        <h4>Tips to improve your search:</h4>
        <ul>
          <li>Check your spelling.</li>
          <li>Try less specific words.</li>
          <li>Try some alternative or substitute words.</li>
          {window.Jbhifi?.letUsKnowUrl ? (
            <li>
              Looking for something awesome that we don’t stock yet?{' '}
              <a style={{ textDecoration: 'underline', display: 'inline' }} href={window.Jbhifi?.letUsKnowUrl}>
                Let us know!
              </a>
            </li>
          ) : null}
        </ul>
      </NoResultsView>
    );
  }

  return (
    <>
      <MemoizedInfiniteResults
        nbHits={nbHits}
        page={page}
        hits={hits}
        showMore={showMore}
        isLastPage={isLastPage}
        isLoading={isLoading}
        productCardVariant={productCardVariant}
        isProductCardDetailsEnabled={isProductCardDetailsEnabled}
        isProductCardDetailBadgesEnabled={isProductCardDetailBadgesEnabled}
      />
      <Disclaimer disclaimers={window.Jbhifi.disclaimers} />
    </>
  );
};

export default CustomInfiniteHits;
